import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

import { authStorage } from 'utils/impersonation';

const axiosServices = axios.create({ baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:3010/' });

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

// Wired up by JWTContext so legacy SWR calls share the same token refresh +
// soft-logout flow as the new apiClient (api/vektra/client.ts).
let onUnauthorized: (() => void) | null = null;
let tokenRefresher: (() => Promise<string | null>) | null = null;

export function setAxiosOnUnauthorized(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export function setAxiosTokenRefresher(fn: (() => Promise<string | null>) | null) {
  tokenRefresher = fn;
}

// Coalesce concurrent refreshes — a single in-flight refresh serves all waiters.
let refreshPromise: Promise<string | null> | null = null;

function requestRefresh(): Promise<string | null> {
  if (!tokenRefresher) return Promise.resolve(null);
  if (!refreshPromise) {
    refreshPromise = tokenRefresher().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

axiosServices.interceptors.request.use(
  async (config) => {
    // Use the same tab-scoped store as JWTContext so impersonation tabs send the
    // impersonated token rather than the admin's localStorage token.
    const accessToken = authStorage().getItem('vektra_access_token');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
}

axiosServices.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config as RetryableConfig | undefined;
    const url: string = original?.url ?? '';
    const isAuthEndpoint = url.startsWith('/auth/') || url.includes('/auth/');

    if (status === 401 && original && !original._retriedAfterRefresh && !isAuthEndpoint) {
      try {
        const newToken = await requestRefresh();
        if (newToken) {
          original._retriedAfterRefresh = true;
          original.headers.set('Authorization', `Bearer ${newToken}`);
          return axiosServices.request(original);
        }
      } catch (refreshError) {
        // VEK-57: the refresh never got a response — the session was not rejected, so don't sign out.
        if (refreshError instanceof TypeError) return Promise.reject(refreshError);
        // otherwise fall through to unauthorized handling
      }
      if (!window.location.pathname.startsWith('/auth/')) {
        onUnauthorized?.();
      }
    }

    return Promise.reject((error?.response && error.response.data) || 'Wrong Services');
  }
);

export default axiosServices;

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get(url, { ...config });

  return res.data;
};

export const fetcherPost = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.post(url, { ...config });

  return res.data;
};

export function redirectWithBasePath(path: string) {
  const basePath = import.meta.env.VITE_APP_BASE_NAME || process.env.VITE_APP_BASE_NAME || ''; // adjust for Vite, CRA, etc.
  window.location.pathname = `${basePath.replace(/\/$/, '')}${path}`;
}
